import { Form, useArrayField, useFormApi } from "informedv4";
import * as React from "react";
import { WebPortalClient, StudyPhase, Phase } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { CorrectedArrayFieldProps } from "../../forms/informedv4/ArrayFieldPropsV4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Modal, { ModalSize } from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { RequiredNotBlank } from "../../forms/validation/Required";

interface StudyPhasesFormState {
    phases: StudyPhase[];
}

interface StudyPhasesProps {
    onClose: () => void;
}

interface StudyPhasesFormProps {
    referencePhases: Phase[];
    onClose: () => void;
}

export const StudyPhasesForm = ({ referencePhases, onClose }: StudyPhasesFormProps) => {
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const { arrayFieldState } = useArrayField({ name: "phases" } as any);
    const formApi = useFormApi();
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    const onUpdate = async () => {
        setSubmitting(true);
        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        try {
            for (const [i, phase] of (state.values.phases as StudyPhase[]).entries()) {
                const existingPhase = await (await client.client.getStudySchedulePhases(undefined, `phaseId eq '${referencePhases[i].id}' and studyId eq ${study.id}`)).value[0];
                if (!existingPhase) {
                    client.client.postStudySchedulePhase({
                        phaseId: referencePhases[i].id,
                        name: phase.name,
                        studyId: study.id,
                    })
                        .then(() => onClose());
                } else {
                    if (existingPhase.name !== phase.name) {
                        client.client.patchStudySchedulePhase({
                            name: phase.name,
                            studyId: study.id,
                        }, existingPhase.id)
                            .then(() => onClose());
                    }
                }

                setSubmitting(false);
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }
    }

    return (
        <div className="modal-body">
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            {arrayFieldState.fields.map((p: CorrectedArrayFieldProps<StudyPhase>, i) => (
                <div key={p.arrayFieldItemState.key} className="row">
                    <div className="col-md-12 form-group">
                        <label htmlFor={`${p.arrayFieldItemState.name}.name`}>
                            {referencePhases.find(rp => rp.id === p.arrayFieldItemState.initialValue.phaseId)?.name}
                        </label>
                        <TextBox
                            name={`${p.arrayFieldItemState.name}.name`}
                            id={`${p.arrayFieldItemState.name}.name`}
                            field={`${p.arrayFieldItemState.name}.name`}
                            validate={RequiredNotBlank}
                        />
                    </div>
                </div>
            ))}
            <div className="row">
                <div className="col-md-12">
                    <div className="pull-right">
                        <AsyncActionButton isSubmitting={submitting} isDisabled={false} className="btn btn-primary" onClick={onUpdate}>
                            Update
                        </AsyncActionButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const StudyPhases = ({ onClose }: StudyPhasesProps) => {
    const client = useWebPortalApiClient();
    const [phases, setPhases] = React.useState<StudyPhase[]>();
    const [refPhases, setRefPhases] = React.useState<Phase[]>();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getPhases = async () => {
            const referencePhasesResult = await client.client.getPhases(undefined, undefined, "displayorder");
            setRefPhases(referencePhasesResult.value);

            const phasesResult = await client.client.getStudySchedulePhases(undefined, `studyId eq ${study.id}`, "displayorder");
            if (phasesResult.value.length === 0)
                setPhases(referencePhasesResult.value.map(p => ({
                    ...p,
                    phaseId: p.id,
                })));
            else
                setPhases(phasesResult.value);
        }

        getPhases();
    }, []);

    let content = null;

    if (!phases || !refPhases)
        content = (<LoadingWithMessage message="Loading Study Phases..." />)
    else
        content = (<Form validateOn="submit" initialValues={{ phases: phases }}>
            <StudyPhasesForm referencePhases={refPhases} onClose={onClose} />
        </Form>);

    return (    
        <Modal show={true} header="Study Phase Labels" size={ModalSize.Normal} closeModalHandler={onClose} automationId="StudyPhasesModal">
            {content}
        </Modal>
    )
}
